import DemoBanner from '@/components/demo/demo-banner';
import PaymentFailedDialog from '@/components/subscription/payment-failed-dialog';
import { AlertsProvider } from '@/modules/alerts/alerts-provider';
import RunWatcher from '@/modules/quant/shared/run-watcher';
import DashboardLayoutModule from '@/modules/dashboard/dashboard-layout';
import { useStoreTransactionSync } from '@/service/app-store/app-store.hook';
import {
  useGetMeQuery,
  useGetUserSubscriptionQuery,
} from '@/service/user/user.hook';
import { useUserPreferencesSync } from '@/service/user/user-preferences.hook';
import { TARGET } from '@/config/platform';
import { UserRole } from '@/types/database-types';
import { Button, CircularProgress } from '@mui/material';
import * as Sentry from '@sentry/react';
import {
  CatchBoundary,
  createFileRoute,
  ErrorRouteComponent,
  Outlet,
} from '@tanstack/react-router';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_dashboard')({
  component: RouteComponent,
});

const ErrorComponent: ErrorRouteComponent = ({ error, reset }) => {
  const { t } = useTranslation();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div className="page-container items-start">
      <h1>{t('error.title')}</h1>
      <p>{t('error.p')}</p>

      <Button onClick={reset}>{t('error.retry')}</Button>
    </div>
  );
};

function RouteComponent() {
  const { data: me, isPending: mePending } = useGetMeQuery();
  const { data: subscription, isPending: subPending } =
    useGetUserSubscriptionQuery();
  // Les pages lisent leurs réglages dans `window.store` au montage : il faut
  // y avoir versé ceux du compte avant de les afficher.
  const { ready: preferencesReady } = useUserPreferencesSync();
  useStoreTransactionSync();

  useEffect(() => {
    if (window.store.get('admin_override_user_id')) return;
    if (subPending || mePending || !me) return;
    posthog.identify(me.id, {
      email: me.email,
      plan: subscription?.plan ?? null,
      extraAccount: subscription?.extraAccounts ?? 0,
      application: TARGET,
      appVersion: window.api.getVersion(),
      isAdmin: me.roles.includes(UserRole.admin),
    });
  }, [me, subscription]);

  return (
    <AlertsProvider>
      <DemoBanner />
      <DashboardLayoutModule>
        <CatchBoundary errorComponent={ErrorComponent} getResetKey={() => ''}>
          {preferencesReady ? (
            <Outlet />
          ) : (
            <div className="flex h-full items-center justify-center">
              <CircularProgress size={32} />
            </div>
          )}
        </CatchBoundary>
      </DashboardLayoutModule>
      <RunWatcher />
      <PaymentFailedDialog />
    </AlertsProvider>
  );
}
